import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { AccountLayout } from '@components/layout/AccountLayout';
import { BrandLoader } from '@components/ui/BrandLoader';
import {
  AddCustomCard,
  AddSpecialtyModal,
  mapSpecialtiesError,
  SpecialtyServiceCard,
  TreatmentsTab,
  useSpecialties,
  useSystemSpecialties,
} from '@features/specialties';
import type { CreateSpecialtyRequest, Specialty, SystemSpecialty } from '@features/specialties';
import { CloseIcon } from '@lib/icons';
import { cn } from '@/styles/tailwind-utils';

type Tab = 'specialties' | 'treatments';

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SpecialtiesPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('specialties');
  const [modalOpen, setModalOpen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const {
    specialties,
    isLoading: isLoadingAccount,
    fetchError: accountFetchError,
    create,
    isCreating,
    createError,
    creatingSpecialtyCode,
    update,
    updatingSpecialtyId,
    updateError,
  } = useSpecialties();

  const {
    systemSpecialties,
    isLoading: isLoadingSystem,
    fetchError: systemFetchError,
  } = useSystemSpecialties();

  // Map specialtyCode → Specialty for all account specialties (including inactive).
  const accountByCode = useMemo(() => {
    const map = new Map<string, Specialty>();
    specialties.forEach((s) => {
      if (s.specialtyCode) map.set(s.specialtyCode, s);
    });
    return map;
  }, [specialties]);

  const systemCodes = useMemo(
    () => new Set(systemSpecialties.map((s) => s.specialtyCode)),
    [systemSpecialties],
  );

  // Account specialties whose specialtyCode does not appear in the system list.
  const customSpecialties = useMemo(
    () => specialties.filter((s) => !s.specialtyCode || !systemCodes.has(s.specialtyCode)),
    [specialties, systemCodes],
  );

  const isMutating = !!creatingSpecialtyCode || !!updatingSpecialtyId;

  function handleSystemToggle(sys: SystemSpecialty) {
    setToggleError(null);
    const existing = accountByCode.get(sys.specialtyCode);

    if (!existing) {
      create(
        { name: sys.name, specialtyCode: sys.specialtyCode, isActive: true, onlineBookingEnabled: true },
        {
          onError: (err) =>
            setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
        },
      );
    } else if (!existing.isActive) {
      update(
        { specialtyId: existing.id, body: { isActive: true } },
        {
          onError: (err) =>
            setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
        },
      );
    } else {
      update(
        { specialtyId: existing.id, body: { isActive: false } },
        {
          onError: (err) =>
            setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
        },
      );
    }
  }

  function handleCustomToggle(s: Specialty) {
    setToggleError(null);
    update(
      { specialtyId: s.id, body: { isActive: !s.isActive } },
      {
        onError: (err) =>
          setToggleError(mapSpecialtiesError(err, t, 'specialties.errors.saveFailed')),
      },
    );
  }

  function handleCreate(data: CreateSpecialtyRequest) {
    create(data, { onSuccess: () => setModalOpen(false) });
  }

  const isPageLoading = isLoadingSystem || isLoadingAccount;
  const pageError = systemFetchError ?? accountFetchError;

  const isModalCreating = isCreating && creatingSpecialtyCode === null;
  const modalCreateError =
    createError && creatingSpecialtyCode === null
      ? mapSpecialtiesError(createError, t, 'specialties.errors.saveFailed')
      : (updateError
          ? mapSpecialtiesError(updateError, t, 'specialties.errors.saveFailed')
          : null);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'specialties', label: t('specialties.tabs.specialties') },
    { id: 'treatments', label: t('specialties.tabs.treatments') },
  ];

  return (
    <AccountLayout>
      <div className="mx-auto max-w-3xl px-4 py-6 md:px-6 md:py-10">
        {/* Header — always visible above tabs */}
        <h1 className="font-display text-2xl font-semibold text-foreground">
          {t('specialties.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('specialties.description')}</p>

        {/* Tab bar */}
        <div className="mt-6 border-b border-border">
          <nav className="flex" aria-label={t('specialties.tabs.label')}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'border-b-2 px-4 pb-3 pt-1 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-brand text-foreground'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="mt-6">
          {activeTab === 'specialties' && (
            <>
              {isPageLoading ? (
                <BrandLoader
                  title={t('specialties.loading.title')}
                  subtitle={t('specialties.loading.subtitle')}
                />
              ) : pageError ? (
                <div
                  role="alert"
                  className="rounded-md bg-destructive/10 p-4 text-sm text-destructive"
                >
                  {mapSpecialtiesError(pageError, t, 'specialties.errors.loadFailed')}
                </div>
              ) : (
                <>
                  {toggleError && (
                    <div
                      role="alert"
                      className="mb-4 flex items-start justify-between gap-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive"
                    >
                      <span>{toggleError}</span>
                      <button
                        type="button"
                        aria-label={t('specialties.dismissError')}
                        onClick={() => setToggleError(null)}
                        className="shrink-0 text-destructive/60 hover:text-destructive"
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  )}

                  {/* System specialties grid */}
                  <div
                    className={cn(
                      'grid grid-cols-3 gap-3',
                      isMutating && 'pointer-events-none',
                    )}
                  >
                    {systemSpecialties.map((sys) => {
                      const existing = accountByCode.get(sys.specialtyCode);
                      const isActive = existing?.isActive === true;
                      const isPending =
                        creatingSpecialtyCode === sys.specialtyCode ||
                        (!!existing && updatingSpecialtyId === existing.id);
                      return (
                        <SpecialtyServiceCard
                          key={sys.id}
                          name={sys.name}
                          icon={sys.icon}
                          isActive={isActive}
                          isPending={isPending}
                          onToggle={() => handleSystemToggle(sys)}
                        />
                      );
                    })}
                    <AddCustomCard onClick={() => setModalOpen(true)} />
                  </div>

                  {/* Custom specialties */}
                  {customSpecialties.length > 0 && (
                    <div className="mt-8">
                      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t('specialties.customSection')}
                      </h2>
                      <div
                        className={cn(
                          'grid grid-cols-3 gap-3',
                          isMutating && 'pointer-events-none',
                        )}
                      >
                        {customSpecialties.map((s) => (
                          <SpecialtyServiceCard
                            key={s.id}
                            name={s.name}
                            icon={s.icon ?? null}
                            isActive={s.isActive}
                            isPending={updatingSpecialtyId === s.id}
                            onToggle={() => handleCustomToggle(s)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'treatments' && (
            <>
              {isLoadingAccount ? (
                <BrandLoader
                  title={t('specialties.loading.title')}
                  subtitle={t('specialties.loading.subtitle')}
                />
              ) : (
                <TreatmentsTab specialties={specialties} />
              )}
            </>
          )}
        </div>
      </div>

      <AddSpecialtyModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleCreate}
        isSubmitting={isModalCreating}
        errorMessage={modalCreateError}
      />
    </AccountLayout>
  );
}
